// SettingsPage.jsx

import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
} from "@mui/material";
import useConfirmDialog from "services/hooks/useConfirmDialog";
import {
  Add,
  Delete,
  Edit,
  Cancel,
  Done,
  Public,
  Group,
  Notifications,
  TrendingUp,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import CustomFieldDefinitionManager from "./CustomFields/CustomFieldDefinitionManager";
import LeadQualificationManager from "./LeadQualification";

const ENDPOINTS = {
  contact_source: "crm/contact-source/",
  contact_status: "crm/contact-status/",
  custom_fields: "crm/custom-field-definitions/",
  lead_scoring_rules: "crm/qualification-rules/",
  reminder_type: "crm/reminder-type/",
  activity_type: "crm/activity-type/",
};

const FIELD_CONFIG = {
  contact_source: {
    label: "Contact Sources",
    endpoint: ENDPOINTS.contact_source,
    supportsColor: false,
  },
  contact_status: {
    label: "Contact Statuses",
    endpoint: ENDPOINTS.contact_status,
    supportsColor: true,
  },
  custom_fields: {
    label: "Custom Fields",
    endpoint: ENDPOINTS.custom_fields,
    supportsColor: true,
  },
  lead_scoring_rules: {
    label: "Lead Scoring Rules",
    endpoint: ENDPOINTS.lead_scoring_rules,
    supportsColor: true,
  },
  reminder_type: {
    label: "Reminder Types",
    endpoint: ENDPOINTS.reminder_type,
    supportsColor: true,
  },
  activity_type: {
    label: "Activity Types",
    endpoint: ENDPOINTS.activity_type,
    supportsColor: true,
  },
};

export default function SettingsPage() {
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [activeFieldType, setActiveFieldType] = useState("contact_status");
  const { ConfirmDialog, showConfirmDialog } = useConfirmDialog();
  const [data, setData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openDialog, setOpenDialog] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [inlineEditData, setInlineEditData] = useState({});

  const config = FIELD_CONFIG[activeFieldType];

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: { label: "", value: "", color: "#06b6d4" } });

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}${config.endpoint}`, {
        headers: { Authorization: `Token ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load:", err);
    }
    setLoading(false);
  }, [domain, token, config.endpoint]);

  useEffect(() => {
    loadData();
  }, [activeFieldType, loadData]);

  useEffect(() => {
    if (editingId && openDialog) {
      const record = data.find((d) => d.id === editingId);
      if (record) {
        setValue("label", record.label);
        setValue("value", record.value);
        setValue("color", record.color || "#06b6d4");
      }
    } else {
      reset();
    }
  }, [data, editingId, openDialog, reset, setValue]);

  const checkDuplicate = async (label, value) => {
    try {
      const res = await axios.get(`${domain}${config.endpoint}`, {
        headers: { Authorization: `Token ${token}` },
      });
      return res.data.some(
        (item) =>
          item.id !== editingId &&
          (item.label.toLowerCase() === label.toLowerCase() ||
            item.value.toLowerCase() === value.toLowerCase())
      );
    } catch {
      return false;
    }
  };

  const formatValue = (val) => {
    return val.trim().toLowerCase().replace(/\s+/g, "_").replace(/_+/g, "_");
  };

  const onSubmit = async (formData) => {
    const isDuplicate = await checkDuplicate(formData.label, formData.value);
    if (isDuplicate) {
      setDuplicateError("Label or Value already exists.");
      return;
    }

    const payload = {
      label: formData.label,
      value: formData.value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_"),
      ...(config.supportsColor && { color: formData.color }),
    };

    try {
      if (editingId) {
        await axios.put(`${domain}${config.endpoint}${editingId}/`, payload, {
          headers: { Authorization: `Token ${token}` },
        });
      } else {
        await axios.post(`${domain}${config.endpoint}`, payload, {
          headers: { Authorization: `Token ${token}` },
        });
      }
      setOpenDialog(false);
      setEditingId(null);
      loadData();
      reset();
      setDuplicateError(null);
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const menuItems = [
    {
      section: "CONTACT MANAGEMENT",
      items: [
        {
          label: "Contact Sources",
          icon: <Public fontSize="small" />,
          type: "contact_source",
          subtitle: "Manage where contacts come from",
        },
        {
          label: "Contact Statuses",
          icon: <Group fontSize="small" />,
          type: "contact_status",
          subtitle: "Define contact lifecycle stages",
        },
        {
          label: "Custom Fields",
          icon: <Group fontSize="small" />,
          type: "custom_fields",
          subtitle:
            "Define global custom fields that can be used across all leads",
        },
        {
          label: "Lead Scoring Rules",
          icon: <Group fontSize="small" />,
          type: "lead_scoring_rules",
          subtitle:
            "Define rules to automatically score leads based on criteria",
        },
      ],
    },
    {
      section: "TASK MANAGEMENT",
      items: [
        {
          label: "Reminder Types",
          icon: <Notifications fontSize="small" />,
          type: "reminder_type",
          subtitle: "Customize reminder categories",
        },
        {
          label: "Activity Types",
          icon: <TrendingUp fontSize="small" />,
          type: "activity_type",
          subtitle: "Define activity categories",
        },
      ],
    },
  ];

  const handleDelete = async (id) => {
    showConfirmDialog({
      title: "Delete Record",
      description: "Are you sure you want to delete this record?",
      onConfirm: async () => {
        try {
          await axios.delete(`${domain}${config.endpoint}${id}/`, {
            headers: { Authorization: `Token ${token}` },
          });
          loadData();
        } catch (err) {
          console.error("Delete error:", err);
          if (err.response && err.response.status === 400) {
            console.log(err);
            const errorMessage = err.response.data?.error;
            toast.error(errorMessage);
          }
        }
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        Customize your CRM system
      </Typography>

      <Box sx={{ display: "flex", gap: 3, mt: 4 }}>
        {/* Left sidebar */}
        <Box sx={{ width: "280px" }}>
          {menuItems.map((group, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600, px: 2 }}
              >
                {group.section}
              </Typography>
              {group.items.map((item) => {
                const isActive = activeFieldType === item.type;
                return (
                  <Box
                    key={item.type}
                    onClick={() => setActiveFieldType(item.type)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      my: 0.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? "action.selected"
                        : "transparent",
                      color: isActive ? "primary.main" : "text.primary",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    {item.icon}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}

          {/* Coming Soon section */}
          {/* <Box
            sx={{
              backgroundColor: "#f9fafb",
              borderRadius: 2,
              px: 2,
              py: 2,
              mt: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Coming Soon
            </Typography>
            <Typography variant="caption" color="text.secondary">
              More customization options like deal stages, email templates, and
              user permissions.
            </Typography>
          </Box> */}
        </Box>
        {/* Main content remains unchanged */}

        <Box sx={{ flex: 1 }}>
          {/* Title + Add Button */}
          {activeFieldType !== "custom_fields" &&
            activeFieldType !== "lead_scoring_rules" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {activeFieldType === "contact_source" && (
                    <Public color="primary" />
                  )}
                  {activeFieldType === "contact_status" && (
                    <Group color="primary" />
                  )}
                  {activeFieldType === "reminder_type" && (
                    <Notifications color="primary" />
                  )}
                  {activeFieldType === "activity_type" && (
                    <TrendingUp color="primary" />
                  )}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {config.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activeFieldType === "contact_status"
                        ? "Define contact lifecycle stages"
                        : activeFieldType === "contact_source"
                        ? "Manage where contacts come from"
                        : activeFieldType === "reminder_type"
                        ? "Customize reminder categories"
                        : "Define activity categories"}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditingId(null);
                    setOpenDialog(true);
                  }}
                  sx={{
                    backgroundColor: "#84DEB8",
                    color: "#000",
                    "&:hover": { backgroundColor: "#6cc9a1" },
                  }}
                >
                  Add {config.label.split(" ")[0]}
                </Button>
              </Box>
            )}
          {activeFieldType === "custom_fields" ? (
            <CustomFieldDefinitionManager />
          ) : activeFieldType === "lead_scoring_rules" ? (
            <LeadQualificationManager />
          ) : (
            <Paper sx={{ p: 3 }}>
              {loading ? (
                // Show Skeleton instead of loader
                <Box>
                  {[...Array(5)].map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, display: "flex", gap: 2 }}>
                        <Box
                          sx={{
                            width: "20%",
                            height: 30,
                            bgcolor: "grey.300",
                            borderRadius: 1,
                          }}
                        />
                        <Box
                          sx={{
                            width: "20%",
                            height: 30,
                            bgcolor: "grey.300",
                            borderRadius: 1,
                          }}
                        />
                        {config.supportsColor && (
                          <Box
                            sx={{
                              width: "20%",
                              height: 30,
                              bgcolor: "grey.300",
                              borderRadius: 1,
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            width: "20%",
                            height: 30,
                            bgcolor: "grey.300",
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: "80px",
                          height: 30,
                          bgcolor: "grey.300",
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : data.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    No data available.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setEditingId(null);
                      setOpenDialog(true);
                    }}
                  >
                    Add {config.label.split(" ")[0]}
                  </Button>
                </Box>
              ) : (
                <>
                  <TableContainer
                    sx={{
                      maxHeight: "calc(100vh - 300px)",
                      overflow: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: "background.paper",
                              fontWeight: "bold",
                              borderBottom: "2px solid",
                              borderColor: "divider",
                            }}
                          >
                            Label
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "background.paper",
                              fontWeight: "bold",
                              borderBottom: "2px solid",
                              borderColor: "divider",
                            }}
                          >
                            Value
                          </TableCell>
                          {config.supportsColor && (
                            <TableCell
                              sx={{
                                backgroundColor: "background.paper",
                                fontWeight: "bold",
                                borderBottom: "2px solid",
                                borderColor: "divider",
                              }}
                            >
                              Color
                            </TableCell>
                          )}
                          <TableCell
                            sx={{
                              backgroundColor: "background.paper",
                              fontWeight: "bold",
                              borderBottom: "2px solid",
                              borderColor: "divider",
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: "background.paper",
                              fontWeight: "bold",
                              borderBottom: "2px solid",
                              borderColor: "divider",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {editingId === item.id ? (
                                  inlineEditData.is_edit ? (
                                    <TextField
                                      size="small"
                                      value={inlineEditData.label || ""}
                                      onChange={(e) =>
                                        setInlineEditData({
                                          ...inlineEditData,
                                          label: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    item.label
                                  )
                                ) : (
                                  item.label
                                )}
                              </TableCell>
                              <TableCell>
                                {editingId === item.id ? (
                                  inlineEditData.is_edit ? (
                                    <TextField
                                      size="small"
                                      value={inlineEditData.value || ""}
                                      onChange={(e) =>
                                        setInlineEditData({
                                          ...inlineEditData,
                                          value: formatValue(e.target.value),
                                        })
                                      }
                                    />
                                  ) : (
                                    <Chip label={item.value} size="small" />
                                  )
                                ) : (
                                  <Chip label={item.value} size="small" />
                                )}
                              </TableCell>
                              {config.supportsColor && (
                                <TableCell>
                                  {editingId === item.id ? (
                                    <input
                                      type="color"
                                      value={inlineEditData.color || "#06b6d4"}
                                      onChange={(e) =>
                                        setInlineEditData({
                                          ...inlineEditData,
                                          color: e.target.value,
                                        })
                                      }
                                      style={{
                                        width: "40px",
                                        height: "36px",
                                        border: "none",
                                        background: "none",
                                        cursor: "pointer",
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label={item.color}
                                      size="small"
                                      sx={{
                                        backgroundColor: item.color || "#ccc",
                                        color: "#fff",
                                        fontWeight: 500,
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              <TableCell>
                                {editingId === item.id ? (
                                  inlineEditData.is_edit ? (
                                    <Mui.Switch
                                      checked={inlineEditData.is_active}
                                      onChange={(e) =>
                                        setInlineEditData({
                                          ...inlineEditData,
                                          is_active: e.target.checked,
                                        })
                                      }
                                      color="success"
                                    />
                                  ) : (
                                    <Chip
                                      label={
                                        item.is_active ? "Active" : "Inactive"
                                      }
                                      size="small"
                                      color={
                                        item.is_active ? "success" : "default"
                                      }
                                    />
                                  )
                                ) : (
                                  <Chip
                                    label={
                                      item.is_active ? "Active" : "Inactive"
                                    }
                                    size="small"
                                    color={
                                      item.is_active ? "success" : "default"
                                    }
                                  />
                                )}
                              </TableCell>

                              <TableCell>
                                {editingId === item.id ? (
                                  <>
                                    <IconButton
                                      onClick={async () => {
                                        const duplicate = await checkDuplicate(
                                          inlineEditData.label,
                                          inlineEditData.value
                                        );
                                        if (duplicate) {
                                          alert(
                                            "Label or Value already exists."
                                          );
                                          return;
                                        }

                                        try {
                                          await axios.put(
                                            `${domain}${config.endpoint}${item.id}/`,
                                            {
                                              label: inlineEditData.label,
                                              value: inlineEditData.value
                                                .trim()
                                                .toLowerCase()
                                                .replace(/\s+/g, "_")
                                                .replace(/_+/g, "_"),
                                              ...(config.supportsColor && {
                                                color: inlineEditData.color,
                                                is_active:
                                                  inlineEditData.is_active,
                                              }),
                                            },
                                            {
                                              headers: {
                                                Authorization: `Token ${token}`,
                                              },
                                            }
                                          );
                                          setEditingId(null);
                                          setInlineEditData({});
                                          loadData();
                                        } catch (err) {
                                          console.error("Update error:", err);
                                        }
                                      }}
                                    >
                                      <Done fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => {
                                        setEditingId(null);
                                        setInlineEditData({});
                                      }}
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </>
                                ) : (
                                  <>
                                    <IconButton
                                      onClick={() => {
                                        setEditingId(item.id);
                                        setInlineEditData({
                                          label: item.label,
                                          value: item.value,
                                          color: item.color || "#06b6d4",
                                          is_active: item.is_active ?? true,
                                          is_edit: item.is_edit,
                                        });
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    {item.is_delete && (
                                      <IconButton
                                        size="small"
                                        title="Delete Activity"
                                        onClick={() => handleDelete(item.id)}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    )}
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <ConfirmDialog />
                  {data.length > rowsPerPage && (
                    <Mui.TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={data.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Dialog remains unchanged */}
      <Dialog
        open={openDialog}
        // onClose={() => setOpenDialog(false)}
        onClose={() => {}}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {editingId
            ? `Edit ${config.label.slice(0, -1)}`
            : `Add New ${config.label.slice(0, -1)}`}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <TextField
              fullWidth
              label="Label"
              placeholder="e.g : Example Value"
              {...register("label", { required: "Label is required" })}
              error={!!errors.label}
              helperText={errors.label?.message}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">Label:</InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Value"
              placeholder="e.g : example_value. Hint:Doesn't accept spaces,Capital letters"
              {...register("value", { required: "Value is required" })}
              error={!!errors.value}
              helperText={errors.value?.message}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">Value:</InputAdornment>
                ),
              }}
            />
            {config.supportsColor && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Color"
                  {...register("color")}
                  type="color"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: watch("color") || "#06b6d4",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
            {duplicateError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {duplicateError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDialog(false)}
              startIcon={<Cancel />}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Done />}
              sx={{
                backgroundColor: "#84DEB8",
                color: "#000",
                "&:hover": { backgroundColor: "#6cc9a1" },
              }}
            >
              {editingId ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
